import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout
import { AppLayout } from './components/Layout';

// Components
import { SubscriptionPrompt } from './components/SubscriptionPrompt';

// Pages
import { LandingPage } from './pages/Landing';
import { LoginPage, RegisterPage, ForgotPasswordPage } from './pages/Auth';
import { DashboardPage } from './pages/Dashboard';
import { WorkersPage } from './pages/Workers';
import { TruckersPage } from './pages/Truckers';
import { OwnersPage } from './pages/Owners';
// Rates page removed - rates are now embedded in day entries
// import { RatesPage } from './pages/Rates';
import { PayrollPeriodsPage, PayrollDetailPage } from './pages/Payroll';
import { ReportsPage } from './pages/Reports';
import { SettingsPage } from './pages/Settings';
import { AdminDashboardPage, AdminSSBPage, AdminUsersPage } from './pages/Admin';

import './App.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
  }

  return children;
}

// Seed data on app load
function DataSeeder() {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API_URL}/seed-data`);
        console.log('Demo data seeded successfully');
      } catch (error) {
        // Silently ignore if already seeded
        console.log('Seed data check complete');
      }
    };
    seedData();
  }, []);

  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />

      {/* Client Routes */}
      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="workers" element={<WorkersPage />} />
        <Route path="truckers" element={<TruckersPage />} />
        <Route path="owners" element={<OwnersPage />} />
        <Route path="payroll" element={<PayrollPeriodsPage />} />
        <Route path="payroll/new" element={<PayrollPeriodsPage />} />
        <Route path="payroll/:id" element={<PayrollDetailPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AppLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="ssb" element={<AdminSSBPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="tenants" element={<AdminDashboardPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <DataSeeder />
          <AppRoutes />
          <Toaster 
            position="top-right" 
            expand={false} 
            richColors 
            closeButton
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--card-foreground))',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
